import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, StarIcon, ShieldCheckIcon, LayoutIcon, CodeIcon, MousePointerClickIcon, LineChartIcon, BriefcaseIcon, CoffeeIcon, WrenchIcon, ShoppingBagIcon, HeartPulseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteLatenMakenPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken | Professioneel webdesign | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Professionele website laten maken? Noord Digital bouwt conversiegerichte websites voor MKB en ZZP in Noord-Holland. Van strategie tot livegang, inclusief SEO.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website laten maken | Professioneel webdesign | Noord',
      'og:description': 'Professionele website laten maken? Noord Digital bouwt conversiegerichte websites voor MKB en ZZP in Noord-Holland.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/website-laten-maken',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Wat kost een website laten maken?',
    a: "De kosten voor een website variëren afhankelijk van je wensen, het aantal pagina's en de benodigde functionaliteiten. We bieden altijd een transparante prijsopgave vooraf, zodat je precies weet waar je aan toe bent zonder verborgen kosten."
  }, {
    q: 'Hoe lang duurt het om een website te maken?',
    a: 'Een standaard bedrijfswebsite is vaak binnen 2 tot 4 weken live. Voor complexere websites of maatwerk oplossingen plannen we samen een realistische tijdlijn in.'
  }, {
    q: 'Kan ik zelf de website aanpassen?',
    a: 'Ja, wij bouwen onze websites met gebruiksvriendelijke systemen (zoals WordPress). Je krijgt na oplevering uitleg zodat je zelf eenvoudig teksten en afbeeldingen kunt aanpassen.'
  }, {
    q: 'Is SEO inbegrepen?',
    a: 'Absoluut. Elke website die wij opleveren is technisch geoptimaliseerd voor zoekmachines. We zorgen voor de juiste structuur, snelle laadtijden en basis on-page SEO zodat je direct goed vindbaar bent.'
  }, {
    q: 'Wat gebeurt er na oplevering?',
    a: 'Na livegang laten we je niet in de steek. We bieden betrouwbare hosting en technisch onderhoud aan, zodat je website altijd snel, veilig en up-to-date blijft.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2445">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2446">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2447">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2448" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2449" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2450" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2451">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2452">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2453">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2454">/</span>
            <span className="text-accent-light font-medium" data-id="element-2455">
              Website laten maken
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2456">
            Website laten maken die klanten oplevert
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2457">
            Professionele websites voor bedrijven die online willen groeien. Wij
            ontzorgen je volledig, van strategie tot livegang.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2458">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2459">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-2460" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom Section */}
      <section className="py-24 bg-surface-muted" data-id="element-2461">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2462">
          <div className="text-center mb-16" data-id="element-2463">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2464">
              Waarom een goede website belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2465">
              Een sterke online aanwezigheid is onmisbaar voor de groei van jouw
              bedrijf.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2466">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2467">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2468">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-2469" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2470">
                Meer aanvragen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2471">
                Zet websitebezoekers om in betalende klanten met een
                conversiegerichte aanpak.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2472">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2473">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2474" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2475">
                Betere vindbaarheid
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2476">
                Word lokaal en regionaal beter gevonden in Google door
                potentiële klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2477">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2478">
                <StarIcon className="w-6 h-6 text-accent" data-id="element-2479" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2480">
                Professionele uitstraling
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2481">
                Maak een onuitwisbare eerste indruk met een modern en strak
                webdesign.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2482">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2483">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2484" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2485">
                Meer vertrouwen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2486">
                Straal autoriteit uit en overtuig bezoekers van jouw expertise
                en kwaliteit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat wij bouwen */}
      <section className="py-24 bg-white" data-id="element-2487">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2488">
          <div className="text-center mb-16" data-id="element-2489">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2490">
              Wat wij bouwen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2491">
              Oplossingen op maat voor jouw specifieke bedrijfsdoelen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2492">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2493">
              <div className="flex items-start gap-4" data-id="element-2494">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2495">
                  <LayoutIcon className="w-6 h-6 text-navy-700" data-id="element-2496" />
                </div>
                <div data-id="element-2497">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2498">
                    Bedrijfswebsite
                  </h3>
                  <p className="text-slate-600" data-id="element-2499">
                    Een compleet online visitekaartje dat jouw diensten,
                    projecten en bedrijfscultuur perfect presenteert.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2500">
              <div className="flex items-start gap-4" data-id="element-2501">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2502">
                  <CodeIcon className="w-6 h-6 text-navy-700" data-id="element-2503" />
                </div>
                <div data-id="element-2504">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2505">
                    WordPress website
                  </h3>
                  <p className="text-slate-600" data-id="element-2506">
                    Flexibele, makkelijk te beheren websites gebouwd op het
                    meest populaire CMS ter wereld.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2507">
              <div className="flex items-start gap-4" data-id="element-2508">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2509">
                  <MousePointerClickIcon className="w-6 h-6 text-navy-700" data-id="element-2510" />
                </div>
                <div data-id="element-2511">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2512">
                    Landingspagina's
                  </h3>
                  <p className="text-slate-600" data-id="element-2513">
                    Doelgerichte pagina's speciaal ontworpen voor
                    advertentiecampagnes en maximale leadgeneratie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-2514">
              <div className="flex items-start gap-4" data-id="element-2515">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-2516">
                  <LineChartIcon className="w-6 h-6 text-navy-700" data-id="element-2517" />
                </div>
                <div data-id="element-2518">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2519">
                    Conversie geoptimaliseerde websites
                  </h3>
                  <p className="text-slate-600" data-id="element-2520">
                    Websites waarbij elke knop, tekst en afbeelding is ingericht
                    om de bezoeker tot actie te laten overgaan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proces */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2521">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2522" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2523">
          <div className="text-center mb-16" data-id="element-2524">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2525">
              Ons proces
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2526">
              In 5 heldere stappen naar jouw nieuwe website.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2527">
            {['Kennismaking', 'Strategie', 'Ontwerp', 'Ontwikkeling', 'Livegang'].map((step, i) => <div key={step} className="bg-white/[0.06] border border-white/10 p-6 rounded-2xl text-center relative" data-id="element-2528">
                <span className="absolute top-2 right-4 text-4xl font-bold text-white/[0.05]" data-id="element-2529">
                  {i + 1}
                </span>
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-2530">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold" data-id="element-2531">{step}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-surface-muted" data-id="element-2532">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2533">
          <div className="text-center mb-16" data-id="element-2534">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2535">
              Voor wie bouwen wij?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2536">
              Wij hebben ervaring in diverse sectoren.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-2537">
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2538">
              <BriefcaseIcon className="w-5 h-5 text-accent" data-id="element-2539" />
              <span className="font-medium text-navy-900" data-id="element-2540">
                Zakelijke dienstverlening
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2541">
              <CoffeeIcon className="w-5 h-5 text-accent" data-id="element-2542" />
              <span className="font-medium text-navy-900" data-id="element-2543">Horeca</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2544">
              <WrenchIcon className="w-5 h-5 text-accent" data-id="element-2545" />
              <span className="font-medium text-navy-900" data-id="element-2546">
                Bouw & installatie
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2547">
              <ShoppingBagIcon className="w-5 h-5 text-accent" data-id="element-2548" />
              <span className="font-medium text-navy-900" data-id="element-2549">Retail</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-2550">
              <HeartPulseIcon className="w-5 h-5 text-accent" data-id="element-2551" />
              <span className="font-medium text-navy-900" data-id="element-2552">Zorg</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2553">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2554">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2555">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2556">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2557">
              WordPress website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2558">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2559">
              Wat kost een website?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2560">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2561">
              Webdesigner in Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2562">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2563">
              Hosting & technisch beheer
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2564">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2565">
          <div className="text-center mb-16" data-id="element-2566">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2567">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2568">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2569">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2570">
                  <span className="font-bold text-navy-900" data-id="element-2571">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2572" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2573" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2574">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2575" />
    </main>;
}